import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, KeyRound, Save } from 'lucide-react';
import Sidebar from './Sidebar';
import UserDropdown from './UserDropdown';
import PasswordInput from './PasswordInput';
import PasswordRequirements from './PasswordRequirements';
import { useAuth } from '../contexts/AuthContext';
import { isPasswordValid, passwordError, PASSWORDS_DO_NOT_MATCH } from '../utils/passwordPolicy';

const EMPTY_FORM = { current_password: '', new_password: '', confirm_password: '' };

// Account menu -> Change Password (any signed-in user).
const ChangePassword = () => {
  const navigate = useNavigate();
  const { changePassword } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const setField = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    setServerErrors({});
    if (field === 'confirm_password') setTouched((t) => ({ ...t, confirm_password: true }));
  };
  const touch = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  const sameAsCurrent = form.new_password && form.new_password === form.current_password;

  const errors = {
    current_password: serverErrors.current_password
      || (touched.current_password && !form.current_password ? 'Current password is required.' : ''),
    new_password: serverErrors.new_password
      || (touched.new_password ? passwordError(form.new_password) : '')
      || (touched.new_password && sameAsCurrent ? 'New password must be different from the current password.' : ''),
    confirm_password: serverErrors.confirm_password
      || (touched.confirm_password && !form.confirm_password ? 'Please confirm the new password.' : '')
      || (touched.confirm_password && form.confirm_password && form.confirm_password !== form.new_password ? PASSWORDS_DO_NOT_MATCH : '')
  };

  const canSubmit = Boolean(form.current_password)
    && isPasswordValid(form.new_password)
    && !sameAsCurrent
    && form.confirm_password === form.new_password
    && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ current_password: true, new_password: true, confirm_password: true });
    if (!canSubmit) return;

    setSaving(true);
    const result = await changePassword(form.current_password, form.new_password, form.confirm_password);
    setSaving(false);

    if (result.success) {
      toast.success(result.message || 'Password changed successfully.');
      setForm(EMPTY_FORM);
      setTouched({});
      return;
    }
    if (result.field) setServerErrors({ [result.field]: result.message });
    toast.error(result.message);
  };

  return (
    <div className="main-container" style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Sidebar />

      <div className="main-content" style={{ flex: 1, padding: '2rem', minWidth: 0 }}>
        <div className="header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <button
                onClick={() => navigate('/dashboard')}
                aria-label="Back to dashboard"
                style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', border: 'none', backgroundColor: 'transparent', color: '#6b7280', cursor: 'pointer', marginRight: '1rem' }}
              >
                <ArrowLeft size={20} />
              </button>
              <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>Change Password</h2>
            </div>
            <p style={{ color: '#6b7280' }}>Update the password you use to sign in.</p>
          </div>
          <div className="user-dropdown">
            <UserDropdown />
          </div>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', border: '1px solid #e5e7eb', padding: '2rem', maxWidth: '520px' }}>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '0.5rem', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <KeyRound size={22} color="#1d4ed8" />
            </div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
              After changing your password you stay signed in here; any other devices or browsers are signed out.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <PasswordInput
              id="cp-current"
              label="Current Password"
              value={form.current_password}
              onChange={setField('current_password')}
              onBlur={touch('current_password')}
              error={errors.current_password}
              autoComplete="current-password"
              placeholder="Enter current password"
            />
            <PasswordInput
              id="cp-new"
              label="New Password"
              value={form.new_password}
              onChange={setField('new_password')}
              onBlur={touch('new_password')}
              error={errors.new_password}
              placeholder="Enter new password"
            />
            <PasswordRequirements password={form.new_password} />
            <PasswordInput
              id="cp-confirm"
              label="Confirm New Password"
              value={form.confirm_password}
              onChange={setField('confirm_password')}
              onBlur={touch('confirm_password')}
              error={errors.confirm_password}
              placeholder="Re-enter new password"
            />

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem 1.5rem',
                backgroundColor: canSubmit ? '#3b82f6' : '#93c5fd',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              <Save size={18} style={{ marginRight: '0.5rem' }} />
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
